import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import {
  Course,
  CourseContent,
  CourseContentType,
  ResourceType,
} from '@/client/courses';
import { QuizType } from '@/client/quizzes';
import { ContentFrameLayoutProvider } from '@/components/courses/content-frame.context';
import { ContentGrayPanel } from '@/components/courses/content-frame.styles';
import { CourseFeedbackModule } from '@/components/courses/CourseFeedbackModule';
import { CourseLanguageSelectScreen } from '@/components/courses/CourseLanguageSelectScreen';
import { CoursePdfModule } from '@/components/courses/CoursePdfModule';
import { CourseQuizModule } from '@/components/courses/CourseQuizModule';
import { CourseSlidesModule } from '@/components/courses/CourseSlidesModule';
import { CourseVideoModule } from '@/components/courses/CourseVideoModule';
import { PreviewLanguageSelector } from '@/components/courses/preview/PreviewLanguageSelector';
import { ScormModulePreview } from '@/components/courses/preview/ScormModulePreview';
import {
  useCourseResource,
  usePrefetchCourseQuiz,
  usePrefetchCourseResource,
  usePrefetchSlideDeck,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useContentViewerActiveClass } from '@/hooks/useContentViewerActiveClass';
import { useToast } from '@/hooks/useToast';
import { selectCurrentUser } from '@/store/features/users';
import { FlexContainer } from '@/ui/styled-ui';
import { AxiosError } from 'axios';
import classNames from 'classnames';

import {
  containsScormModule,
  determinePreselectedCourseLanguage,
  isRtlLanguage,
} from '@/utils/helpers';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CourseContentPreview: React.FC<{
  course: Course;
  initialModuleIndex?: number;
  className?: string;
  isPreviewModal?: boolean;
  isMaximized?: boolean;
  language?: LanguagesEnum;
  onModuleAdvance?: () => void;
  onExitPreview?: () => void;
}> = ({
  course,
  initialModuleIndex = 0,
  className,
  isPreviewModal,
  isMaximized,
  language,
  onModuleAdvance,
  onExitPreview,
}) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const { t } = useTranslation();
  const toast = useToast();

  const [isCourseStarted, setIsCourseStarted] = useState<boolean>(
    !!isPreviewModal ||
      !!(course?.selfHosted && course?.courseLanguages?.length === 1),
  );
  const [selectedLanguage, setSelectedLanguage] = useState<LanguagesEnum>(
    language ?? determinePreselectedCourseLanguage(currentUser, course),
  );
  const isRtl = isRtlLanguage(selectedLanguage);

  const modules = useMemo(
    () =>
      [...(course?.courseContents ?? [])].sort(
        (a, b) => a.position - b.position,
      ),
    [course?.courseContents],
  );
  const [currentModuleIndex, setCurrentModuleIndex] =
    useState<number>(initialModuleIndex);
  const currentModule = modules[currentModuleIndex];

  const currentResourceId =
    currentModule?.type === CourseContentType.RESOURCE
      ? currentModule.resource?.id
      : undefined;

  const { resource: currentResource } = useCourseResource({
    courseId: course?.id,
    resourceId: currentResourceId,
    staleTime: Infinity,
    cacheTime: Infinity,
    onError: (e) => handleAxiosError(e as Error | AxiosError, toast),
  });

  const prefetchCourseResource = usePrefetchCourseResource();
  const prefetchSlideDeck = usePrefetchSlideDeck();
  const prefetchCourseQuiz = usePrefetchCourseQuiz();

  const prefetchNextModule = (nextModule: CourseContent) => {
    if (nextModule.type === CourseContentType.RESOURCE) {
      prefetchCourseResource(course?.id, nextModule.resource?.id);
    }

    if (nextModule.type === CourseContentType.PRESENTATION) {
      prefetchSlideDeck(course?.id, nextModule.presentation?.id);
    }

    if (nextModule.quiz) {
      prefetchCourseQuiz(course?.id, nextModule.quiz.id);
    }
  };

  useEffect(() => {
    const nextModule = modules[currentModuleIndex + 1];
    if (nextModule) prefetchNextModule(nextModule);
  }, [course?.id, currentModuleIndex, modules]);

  const advanceToNextModule = () => {
    if (currentModuleIndex !== modules.length - 1) {
      setCurrentModuleIndex(currentModuleIndex + 1);
      onModuleAdvance?.();
    }
  };

  const notifyContentCompletion = () => {
    const isTraditionalQuiz =
      currentModule?.quiz?.type === QuizType.TRADITIONAL;
    const isLastModule = currentModuleIndex === modules.length - 1;
    const courseHasNoQuizOrScorm =
      !modules.some((module) => module.quiz?.type === QuizType.TRADITIONAL) &&
      !containsScormModule(modules);

    if (isTraditionalQuiz || isLastModule || courseHasNoQuizOrScorm) {
      toast?.success(t('toast.success'), t('content.completed.success'));
    }
  };

  const handleModuleCompletion = () => {
    notifyContentCompletion();
    advanceToNextModule();
  };

  // "Finish" on the final module. In the preview modal, finishing the content
  // closes the overlay instead of trying (and failing) to advance past the end.
  const handleFinish = () => {
    notifyContentCompletion();
    const isLastModule = currentModuleIndex === modules.length - 1;
    if (isPreviewModal && isLastModule && onExitPreview) {
      onExitPreview();
      return;
    }
    advanceToNextModule();
  };

  const handleBackToCourse = async () => {
    setCurrentModuleIndex(0);
  };

  useEffect(() => {
    if (language) setSelectedLanguage(language);
  }, [language]);

  useEffect(() => {
    if (course?.selfHosted && course?.courseLanguages?.length === 1) {
      setIsCourseStarted(true);
    }
  }, [course?.selfHosted, course?.courseLanguages?.length]);

  useEffect(() => {
    if (course?.courseContents?.length) {
      setCurrentModuleIndex(initialModuleIndex);
    }
  }, [course?.id, initialModuleIndex]);

  useContentViewerActiveClass();

  return (
    <ContentFrameLayoutProvider framedScroll>
      <FlexContainer
        translate="no"
        direction="column"
        align="flex-start"
        justify="flex-start"
        className={classNames('notranslate', className)}
        style={{ minHeight: 0, position: 'relative' }}
      >
        {isPreviewModal && course.courseLanguages && (
          <PreviewLanguageSelector
            languages={course.courseLanguages}
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
          />
        )}

        {!isCourseStarted &&
          course.selfHosted &&
          !!course?.courseContents?.length && (
            <CourseLanguageSelectScreen
              course={course}
              initialLanguage={selectedLanguage}
              isDisabled={!modules?.length}
              setIsCourseStarted={setIsCourseStarted}
              setSelectedLanguage={setSelectedLanguage}
            />
          )}

        {isCourseStarted && (
          <>
            {currentModule?.quiz?.type === QuizType.TRADITIONAL && (
              <CourseQuizModule
                course={course}
                quizId={currentModule?.quiz?.id}
                attemptLanguage={selectedLanguage || LanguagesEnum.EN}
                hasPreviousModules={currentModuleIndex > 0}
                isPreview
                onFinish={handleFinish}
                handleBackToCourse={handleBackToCourse}
                rtl={isRtl}
              />
            )}

            {currentModule?.type === CourseContentType.RESOURCE &&
              currentResource?.type === ResourceType.VIDEO && (
                <CourseVideoModule
                  key={currentModuleIndex}
                  moduleContent={{
                    ...currentModule,
                    resource: currentResource,
                  }}
                  language={selectedLanguage}
                  currentModuleIndex={currentModuleIndex}
                  modules={modules}
                  isContentStarted={isCourseStarted}
                  rtl={isRtl}
                  isCompact={!!isPreviewModal && !isMaximized}
                  containerMaxWidth={undefined}
                  onLanguageChange="resume"
                  onContinue={advanceToNextModule}
                  onFinish={handleFinish}
                />
              )}

            {currentModule?.type === CourseContentType.PRESENTATION && (
              <CourseSlidesModule
                key={currentModuleIndex}
                presentationId={currentModule?.presentation?.id}
                courseId={course?.id}
                language={selectedLanguage}
                isAuthoringPreview
                currentModuleIndex={currentModuleIndex}
                modules={modules}
                isContentStarted={isCourseStarted}
                rtl={isRtl}
                onContinue={advanceToNextModule}
                onFinish={handleFinish}
                onPreviousModule={() => {
                  if (currentModuleIndex > 0) {
                    setCurrentModuleIndex(currentModuleIndex - 1);
                  }
                }}
              />
            )}

            {currentModule?.type === CourseContentType.RESOURCE &&
              currentResource?.type === ResourceType.PDF && (
                <CoursePdfModule
                  key={currentModuleIndex}
                  resource={currentResource}
                  requiresReadConfirmation={
                    !!currentResource.requiresReadConfirmation
                  }
                  language={selectedLanguage}
                  currentModuleIndex={currentModuleIndex}
                  modules={modules}
                  isContentStarted={isCourseStarted}
                  rtl={isRtl}
                  onContinue={advanceToNextModule}
                  onFinish={handleFinish}
                  onPreviousModule={() => {
                    if (currentModuleIndex > 0) {
                      setCurrentModuleIndex(currentModuleIndex - 1);
                    }
                  }}
                />
              )}

            {currentModule?.quiz?.type === QuizType.FEEDBACK && (
              <CourseFeedbackModule
                course={course}
                quizFeedbackId={currentModule?.quiz?.id}
                isPreview
                attemptLanguage={selectedLanguage}
                onComplete={handleModuleCompletion}
                onExit={onExitPreview}
                rtl={isRtl}
              />
            )}
          </>
        )}

        {(isCourseStarted || !course.selfHosted) &&
          currentResource?.type === ResourceType.SCORM &&
          currentUser && (
            <ScormModulePreview
              key={currentModuleIndex}
              course={course}
              resourceId={currentResource.id}
              selectedLanguage={selectedLanguage}
              currentUser={currentUser}
              isLastModule={currentModuleIndex === modules.length - 1}
              onNextModule={() => setCurrentModuleIndex(currentModuleIndex + 1)}
            />
          )}

        {!course?.courseContents?.length &&
          (course.courseThumbnail?.signedUrl ? (
            <img
              src={course.courseThumbnail.signedUrl}
              alt={course.name}
              style={{ width: '100%', objectFit: 'contain' }}
            />
          ) : (
            <ContentGrayPanel direction="column" flex="1" height="100%">
              <h3>{t('course.scorm.preview.noFiles')}</h3>
            </ContentGrayPanel>
          ))}
      </FlexContainer>
    </ContentFrameLayoutProvider>
  );
};
